import { faStar } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  StatCard,
  StatGrid,
  Strong,
} from '../../components/ui';
import { useHostStayReviewsQuery } from '../../services/stayService/__generated__/queries.generated';
import { GUEST_REVIEW_CATEGORIES, averageLabel } from '../StayBookingPage/stayLifecycle';
import { stayErrorMessage } from './stayErrors';

const PAGE = 20;

/**
 * Reviews guests wrote about the host's places (spec 016 W22). Only reviews that show
 * are listed: both sides reviewed, or the 14-day window closed.
 */
export default function HostReviewsPage() {
  const { user } = useSnapshot(authStore);
  const { data, loading, error, fetchMore } = useHostStayReviewsQuery({
    variables: { hostId: user?.id ?? '', limit: PAGE, offset: 0 },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });
  const result = data?.getHostRatings;
  const reviews = (result?.reviews ?? []).filter(review => review.rating.stayGuestRating);

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Hosting"
        title="Reviews"
        description="What guests said about your places. A review shows once you both have written one, or after 14 days."
      />
      {error && !result ? (
        <Alert tone="error" title="Could not load">
          {stayErrorMessage(error)}
        </Alert>
      ) : null}
      {(loading || !user?.id) && !result ? <LoadingBlock label="Loading reviews" /> : null}
      {result ? (
        <Stack $gap={6} data-testid="stay-host-reviews">
          <StatGrid $cols={2}>
            <StatCard label="Overall rating" value={averageLabel(result.summary.averageRating)} />
            <StatCard label="Reviews" value={String(result.summary.totalRatings)} />
          </StatGrid>
          {reviews.length === 0 ? (
            <EmptyState
              icon={faStar}
              title="No reviews yet"
              description="After a stay ends, ask your guests to leave a review."
            />
          ) : (
            reviews.map(review => {
              const stay = review.rating.stayGuestRating!;
              return (
                <Card key={review.rating.id} data-testid="stay-host-review">
                  <CardBody>
                    <Stack $gap={4}>
                      <div>
                        <Strong>
                          {review.guestName} · {'★'.repeat(stay.overall)}
                        </Strong>
                        <div>
                          <Muted $size="sm">
                            {[
                              review.carName,
                              DateTime.fromISO(String(review.tripDate)).toFormat('LLLL yyyy'),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Muted>
                        </div>
                      </div>
                      {stay.publicReview ? <p>{stay.publicReview}</p> : null}
                      <Muted $size="sm">
                        {GUEST_REVIEW_CATEGORIES.map(c => `${c.label} ${stay[c.key]}`).join(' · ')}
                      </Muted>
                    </Stack>
                  </CardBody>
                </Card>
              );
            })
          )}
          {result.totalCount > (result.reviews?.length ?? 0) ? (
            <div>
              <Button
                theme="secondary"
                text="Show more"
                onClick={() =>
                  fetchMore({
                    variables: { offset: result.reviews.length },
                    updateQuery: (prev, { fetchMoreResult }) => ({
                      getHostRatings: {
                        ...fetchMoreResult.getHostRatings,
                        reviews: [
                          ...prev.getHostRatings.reviews,
                          ...fetchMoreResult.getHostRatings.reviews,
                        ],
                      },
                    }),
                  })
                }
              />
            </div>
          ) : null}
        </Stack>
      ) : null}
    </PageContainer>
  );
}
